import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { AccountRole, AdminPermission, APP_FEATURE_DEFAULTS, AppFeature, AppFeatureToggles } from '@ivy-monorepo/shared'
import { authService } from '../services/auth.service'
import { LocalStorageKey } from '../helpers/constants'
import { LoginResponseDto } from '@ivy-monorepo/shared'

interface PermissionsContextValue {
  permissions: AdminPermission[]
  /** Feature Flags kill switches from admin `/me` (defaults until loaded). */
  features: AppFeatureToggles
  /** Keeps [features] in step after a save on the Feature Flags page. */
  setFeatureEnabled: (feature: AppFeature, enabled: boolean) => void
  isLoading: boolean
  hasPermission: (permission: AdminPermission) => boolean
  refreshPermissions: () => Promise<void>
}

const PermissionsContext = createContext<PermissionsContextValue | null>(null)

export const PermissionsProvider = ({ children }: { children: React.ReactNode }) => {
  const [permissions, setPermissions] = useState<AdminPermission[]>([])
  const [features, setFeatures] = useState<AppFeatureToggles>(APP_FEATURE_DEFAULTS)
  const [isLoading, setIsLoading] = useState(true)

  const refreshPermissions = useCallback(async () => {
    const authDataFromStorage = localStorage.getItem(LocalStorageKey.auth)
    if (!authDataFromStorage) {
      setPermissions([])
      setIsLoading(false)
      return
    }

    const authData = JSON.parse(authDataFromStorage) as LoginResponseDto
    if (authData.account.role !== AccountRole.Admin) {
      setPermissions([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    try {
      const me = await authService.getMe()
      setPermissions(me?.permissions ?? [])
      if (me?.features) setFeatures(me.features)
    } catch {
      setPermissions([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    refreshPermissions()
  }, [refreshPermissions])

  const hasPermission = useCallback(
    (permission: AdminPermission) => permissions.includes(permission),
    [permissions]
  )

  const setFeatureEnabled = useCallback((feature: AppFeature, enabled: boolean) => {
    setFeatures(prev => ({ ...prev, [feature]: enabled }))
  }, [])

  const value = useMemo(
    () => ({ permissions, features, setFeatureEnabled, isLoading, hasPermission, refreshPermissions }),
    [permissions, features, setFeatureEnabled, isLoading, hasPermission, refreshPermissions]
  )

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>
}

export const usePermissions = (): PermissionsContextValue => {
  const context = useContext(PermissionsContext)
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider')
  }
  return context
}
